import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../../assets/images/logo.jpg';
import { trackButtonClick } from '../../utils/analytics';

const isLoggedIn = () => {
  return !!localStorage.getItem("token");
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    closeMenu();
    trackButtonClick(targetId, 'header', 'navigation_click');

    if (location.pathname !== '/') {
      window.location.href = '/#' + targetId;
      return;
    }

    const element = document.getElementById(targetId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleContactClick = () => {
    closeMenu();
    trackButtonClick('contact', 'header', 'navigation_click');
  };

  const handleSocialClick = (platform: string, isMobile: boolean = false) => {
    const location = isMobile ? 'header_mobile' : 'header';
    trackButtonClick(platform, location, 'social_click');
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        <div className="header-logo">
          <img src={logo} alt="Gelwel" className="header-logo-image" />
          <span className="header-logo-text">Gelwel</span>
        </div>

        <nav className="header-nav">
          <a href="#home" className="header-nav-link" onClick={(e) => handleNavClick(e, 'home')}>
            Home
          </a>
          <a href="#about" className="header-nav-link" onClick={(e) => handleNavClick(e, 'about')}>
            About
          </a>
          <Link to="/contact" className="header-nav-link" onClick={handleContactClick}>
            Contact
          </Link>
          <div className="header-social">
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer"
              className="header-social-link"
              aria-label="TikTok"
              onClick={() => handleSocialClick('tiktok')}
            >
              <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="header-social-link"
              aria-label="Instagram"
              onClick={() => handleSocialClick('instagram')}
            >
              <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="header-social-link"
              aria-label="X (Twitter)"
              onClick={() => handleSocialClick('twitter')}
            >
              <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
          </div>
          {/* AUTH ACTIONS */}
          <div className="header-auth-actions">
            {!isLoggedIn() ? (
              <>
                <Link to="/login" className="header-auth-btn header-login-btn">
                  Login
                </Link>
                <Link to="/register" className="header-auth-btn header-register-btn">
                  Register
                </Link>
              </>
            ) : (
              <button
                className="header-auth-btn header-logout-btn"
                onClick={handleLogout}
              >
                Logout
              </button>
            )}
          </div>

        </nav>

        <button
          className={`header-hamburger ${isMenuOpen ? 'header-hamburger-active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span className="header-hamburger-line"></span>
          <span className="header-hamburger-line"></span>
          <span className="header-hamburger-line"></span>
        </button>

        {isMenuOpen && (
          <div className="header-overlay" onClick={closeMenu} aria-hidden="true"></div>
        )}

        <div className={`header-mobile-menu ${isMenuOpen ? 'header-mobile-menu-open' : ''}`}>
          <button
            className="header-mobile-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            ×
          </button>
          <nav className="header-mobile-nav">
            <a href="#home" className="header-mobile-link" onClick={(e) => handleNavClick(e, 'home')}>
              Home
            </a>
            <a href="#about" className="header-mobile-link" onClick={(e) => handleNavClick(e, 'about')}>
              About
            </a>
            <Link to="/contact" className="header-mobile-link" onClick={handleContactClick}>
              Contact
            </Link>
            <div className="header-mobile-social">
              <a
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
                className="header-social-link"
                aria-label="TikTok"
                onClick={() => handleSocialClick('tiktok', true)}
              >
                <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="header-social-link"
                aria-label="Instagram"
                onClick={() => handleSocialClick('instagram', true)}
              >
                <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="header-social-link"
                aria-label="X (Twitter)"
                onClick={() => handleSocialClick('twitter', true)}
              >
                <svg className="header-social-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
            {/* AUTH ACTIONS MOBILE */}
            <div className="header-mobile-auth">
              {!isLoggedIn() ? (
                <>
                  <Link to="/login" className="header-mobile-auth-btn" onClick={closeMenu}>
                    Login
                  </Link>
                  <Link to="/register" className="header-mobile-auth-btn" onClick={closeMenu}>
                    Register
                  </Link>
                </>
              ) : (
                <button
                  className="header-mobile-auth-btn logout"
                  onClick={() => {
                    closeMenu();
                    handleLogout();
                  }}
                >
                  Logout
                </button>
              )}
            </div>

          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
